/**
 * Client-Side Debug Logger (React + TypeScript)
 *
 * Responsibilities:
 * - Override console methods (log/info/warn/error/debug) ONLY when debug mode is enabled.
 * - Maintain a small in-memory circular buffer (default 600 entries).
 * - Capture useful context: timestamp, url, stack traces for errors, user agent, etc.
 * - Strong redaction: never store passwords, tokens, or long secrets in the buffer.
 * - Reliable flush:
 *     • navigator.sendBeacon (preferred for pagehide / tab close on mobile + desktop)
 *     • fetch with keepalive as fallback
 * - Listen to visibilitychange (hidden) + pagehide + beforeunload for best-effort delivery
 *   even when the user closes the tab or the app is backgrounded on iOS Safari / Android.
 * - Debug activation (all require explicit user intent or dev action):
 *     • ?debug=true in URL (one-time enable + sets localStorage)
 *     • localStorage key "client_debug" === "1"
 *     • Programmatic via setEnabled(true)
 *
 * This is a diagnostic / support tool. It is 100% opt-in.
 * See Privacy Policy "Telemetry & Diagnostics" + "Provide Customer Support".
 *
 * No data is sent automatically except on explicit "Send to Support" or
 * the minimal reliability flushes while the user has debug mode active.
 */

export type LogLevel = "log" | "info" | "warn" | "error" | "debug";

export type ClientLogEntry = {
  id: string;
  ts: string;           // ISO string from client
  level: LogLevel;
  message: string;
  args?: unknown[];
  stack?: string;
  url?: string;
} & Record<string, unknown>; // allow future extensions via arbitrary extra keys


export interface ClientLogBatch {
  logs: ClientLogEntry[];
  meta: Record<string, unknown>;
  userNote?: string;
}

const STORAGE_KEY = "client_debug";
const MAX_BUFFER = 600;
const MAX_ENTRY_MESSAGE = 4000;
const MAX_ARG_LENGTH = 2000;

let originals: Partial<typeof console> = {};
let buffer: ClientLogEntry[] = [];
let currentSnapshot: ClientLogEntry[] = [];
let enabled = false;
let listeners: Set<() => void> = new Set();
let listenersInstalled = false;
let overrideInstalled = false;

// ---------------------------------------------------------------------------
// Safe serialization + redaction (critical for privacy)
// ---------------------------------------------------------------------------

function looksLikeSecret(value: string): boolean {
  const v = value.toLowerCase();
  return (
    v.includes("token") ||
    v.includes("bearer") ||
    v.includes("password") ||
    v.includes("secret") ||
    v.includes("apikey") ||
    v.includes("api_key") ||
    v.includes("authorization") ||
    /^[a-z0-9-_]{32,}$/i.test(value) // long opaque strings
  );
}

function safeStringify(value: unknown, seen = new WeakSet<object>()): string {
  try {
    if (value === null || value === undefined) return String(value);
    if (typeof value === "string") {
      if (looksLikeSecret(value)) return "[REDACTED_SECRET]";
      return value.length > MAX_ARG_LENGTH
        ? value.slice(0, MAX_ARG_LENGTH) + "…[truncated]"
        : value;
    }
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "function") return "[Function]";
    if (value instanceof Error) {
      return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ""}`;
    }

    if (typeof value === "object") {
      if (seen.has(value as object)) return "[Circular]";
      seen.add(value as object);

      // DOM nodes / React fibers etc. — be extremely conservative
      if (
        (value as any).nodeType ||
        (typeof HTMLElement !== "undefined" && value instanceof HTMLElement)
      ) {
        return `[DOM:${(value as any).tagName || "Element"}]`;
      }

      const out: Record<string, string> = {};
      let count = 0;
      for (const k of Object.keys(value as object)) {
        if (count++ > 12) {
          out["__truncated"] = "true";
          break;
        }
        const v = (value as any)[k];
        if (typeof k === "string" && looksLikeSecret(k)) {
          out[k] = "[REDACTED_KEY]";
          continue;
        }
        out[k] = safeStringify(v, seen);
      }
      return JSON.stringify(out);
    }
    return String(value);
  } catch {
    return "[Unserializable]";
  }
}

function formatMessage(args: unknown[]): { message: string; args: unknown[] } {
  if (args.length === 0) return { message: "", args: [] };
  const first = args[0];
  if (typeof first === "string") {
    return {
      message: first.slice(0, MAX_ENTRY_MESSAGE),
      args: args.slice(1).map((a) => safeStringify(a)),
    };
  }
  return {
    message: safeStringify(first),
    args: args.slice(1).map((a) => safeStringify(a)),
  };
}

// ---------------------------------------------------------------------------
// Buffer management (circular)
// ---------------------------------------------------------------------------

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function pushEntry(entry: Omit<ClientLogEntry, "id">) {
  const full: ClientLogEntry = {
    ...entry,
    id: makeId(),
  } as ClientLogEntry;
  buffer.push(full);
  if (buffer.length > MAX_BUFFER) {
    buffer.shift();
  }
  currentSnapshot = [...buffer];
  // Notify React listeners (live viewer)
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
}

export function getBuffer(): ClientLogEntry[] {
  return currentSnapshot;
}

export function clearBuffer(): void {
  buffer = [];
  currentSnapshot = [];
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
}

// ---------------------------------------------------------------------------
// Console override / restore
// ---------------------------------------------------------------------------

function installOverrides() {
  if (overrideInstalled) return;
  originals = {
    log: console.log.bind(console),
    info: console.info?.bind(console) || console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    debug: console.debug?.bind(console) || console.log.bind(console),
  };

  const levels: LogLevel[] = ["log", "info", "warn", "error", "debug"];

  levels.forEach((level) => {
    const orig = originals[level] || originals.log!;
    (console as any)[level] = (...args: unknown[]) => {
      // Always call the original so developer still sees output in devtools
      try {
        orig(...args);
      } catch {}

      if (!enabled) return;

      try {
        const { message, args: safeArgs } = formatMessage(args);
        const entry: Omit<ClientLogEntry, "id"> = {
          ts: new Date().toISOString(),
          level,
          message,
          args: safeArgs.length ? safeArgs : undefined,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        };

        // Capture stack for errors when possible
        if (level === "error") {
          const err = args.find((a): a is Error => a instanceof Error);
          if (err?.stack) entry.stack = err.stack.slice(0, 6000);
          else if (typeof args[1] === "string" && args[1].includes("\n")) {
            entry.stack = String(args[1]).slice(0, 6000);
          }
        }

        pushEntry(entry);
      } catch {
        // Never break the app because of logging
      }
    };
  });

  overrideInstalled = true;
}

function restoreOverrides() {
  if (!overrideInstalled) return;
  (["log", "info", "warn", "error", "debug"] as const).forEach((lvl) => {
    const orig = originals[lvl];
    if (orig) {
      try {
        (console as any)[lvl] = orig;
      } catch {}
    }
  });
  originals = {};
  overrideInstalled = false;
}

// ---------------------------------------------------------------------------
// Flush (sendBeacon preferred for unload scenarios)
// ---------------------------------------------------------------------------

const API_BASE = (import.meta.env.VITE_API_URL ?? "/api").replace(/\/$/, "");
const ENDPOINT = `${API_BASE}/client-logs`;
const REFRESH_ENDPOINT = `${API_BASE}/auth/refresh`;

export function clientLog(level: LogLevel, message: string, details?: Record<string, unknown>): void {
  if (!enabled) return;
  try {
    pushEntry({
      ts: new Date().toISOString(),
      level,
      message,
      args: details ? [safeStringify(details)] : undefined,
      url: typeof window !== "undefined" ? window.location.href : undefined,
    });
  } catch {
    // Diagnostic logging must never affect app behavior.
  }
}

function buildPayload(userNote?: string): ClientLogBatch {
  const meta: Record<string, unknown> = {
    url: typeof window !== "undefined" ? window.location.href : undefined,
    ua: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    viewport:
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}`
        : undefined,
    ts: new Date().toISOString(),
    bufferSize: buffer.length,
  };

  // Clone current buffer (do not drain — user may still want the UI)
  const snapshot = getBuffer();

  return {
    logs: snapshot,
    meta,
    userNote: userNote?.trim() || undefined,
  };
}

export function flush(userNote?: string): void {
  if (buffer.length === 0 && !userNote) return;

  const payload = buildPayload(userNote);
  const body = JSON.stringify(payload);

  // Primary path for mobile reliability + tab close. For explicit support
  // sends (userNote present), prefer fetch below so we can retry after refresh
  // if the access cookie expired while the device was offline.
  if (!userNote && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    } catch {
      // fall through to fetch
    }
  }

  // Fallback (works in most modern browsers for keepalive)
  if (typeof fetch === "function") {
    const fetchOptions: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    };
    fetch(ENDPOINT, fetchOptions).then((response) => {
      if (response.status !== 401 && response.status !== 403) return;
      return fetch(REFRESH_ENDPOINT, {
        method: "POST",
        credentials: "include",
        keepalive: true,
      }).then(() => fetch(ENDPOINT, fetchOptions));
    }).catch(() => {
      // silent – best effort only
    });
  }
}

// ---------------------------------------------------------------------------
// Lifecycle listeners for reliability on mobile/desktop close/background
// ---------------------------------------------------------------------------

function installLifecycleListeners() {
  if (listenersInstalled || typeof window === "undefined") return;

  const onVisibility = () => {
    if (document.visibilityState === "hidden" && enabled) {
      // User switched away or is about to be suspended (very common on mobile)
      flush();
    }
  };

  const onPageHide = () => {
    if (enabled) flush();
  };

  const onBeforeUnload = () => {
    if (enabled) flush();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("beforeunload", onBeforeUnload);

  listenersInstalled = true;

  // Store cleanup for potential future hot-reload (simple, not perfect)
  (window as any).__clientLoggerCleanup = () => {
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("beforeunload", onBeforeUnload);
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function isEnabled(): boolean {
  return enabled;
}

export function setEnabled(next: boolean): void {
  const was = enabled;
  enabled = !!next;

  if (enabled && !was) {
    installOverrides();
    installLifecycleListeners();
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {}
    // seed a marker entry
    pushEntry({
      ts: new Date().toISOString(),
      level: "info",
      message: "[debug] client logger enabled",
      url: typeof window !== "undefined" ? window.location.href : undefined,
    });
  } else if (!enabled && was) {
    restoreOverrides();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
    // Final flush of anything that was captured
    flush();
  }
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function initFromEnvironment(): void {
  if (typeof window === "undefined") return;

  // 1. URL flag (explicit opt-in, common for support sessions)
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "true") {
      localStorage.setItem(STORAGE_KEY, "1");
    }
  } catch {}

  // 2. Persisted preference
  let shouldEnable = false;
  try {
    shouldEnable = localStorage.getItem(STORAGE_KEY) === "1";
  } catch {}

  if (shouldEnable) {
    setEnabled(true);
  }
}

// Allow external code to force a send (used by the panel)
export function sendToSupport(note?: string): void {
  if (!enabled) {
    // If someone calls this while disabled, do nothing (privacy)
    return;
  }
  flush(note);
}

// For tests / advanced use
export function _resetForTests() {
  buffer = [];
  currentSnapshot = [];
  enabled = false;
  listeners.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  restoreOverrides();
  listenersInstalled = false;
  overrideInstalled = false;
}
